import { useEffect, useState } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing } from '../../theming';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { BATCHES_PAGE_SIZES, parseBatches } from '../../utils/vinExtraction';
import { cardStyle } from './vinExtractionStyles';
import { useVinRequest } from './useVinRequest';
import BatchesHeader from './BatchesHeader';
import BatchesBody from './BatchesBody';

/** GET /batches for the range, filter and page; the paging belongs to one range, so a new range starts on page 1. */
const useBatchesQuery = ({ range, refreshToken, onFetched }) => {
    const { getBatches } = useVinExtractionService();
    const [query, setQuery] = useState({ range, result: undefined, page: 1, pageSize: BATCHES_PAGE_SIZES[0] });
    const page = query.range === range ? query.page : 1;
    const { load, run } = useVinRequest({
        request: () =>
            getBatches({ from: range.from, to: range.to, result: query.result, page, pageSize: query.pageSize }),
        parse: parseBatches,
        keepData: true,
        onReady: onFetched,
    });

    useEffect(() => {
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, refreshToken, query.result, page, query.pageSize]);

    const changeQuery = changes => setQuery(prev => ({ ...prev, range, page, ...changes }));

    return { query, page, load, run, changeQuery };
};

/** Batches in the page's range, newest first, from GET /batches; a row expands into its cars panel. */
export default function BatchesSection({ range, refreshToken, onFetched, settings }) {
    const { t } = useTranslation('vinExtraction');
    const batches = useBatchesQuery({ range, refreshToken, onFetched });
    const { load, query, changeQuery } = batches;

    return (
        <Flex
            vertical
            gap={spacing[2]}
            style={cardStyle}
            role="region"
            aria-label={t('batches.aria')}
            aria-busy={load.status === 'loading'}
        >
            <BatchesHeader
                total={load.data ? load.data.total : null}
                settings={settings}
                result={query.result}
                onResultChange={result => changeQuery({ result, page: 1 })}
            />
            <BatchesBody batches={batches} refreshToken={refreshToken} settings={settings} />
        </Flex>
    );
}
